import { useEffect, useState, type CSSProperties } from "react";

import { Header } from "@/components/Header";
import { EmptyState } from "@/components/EmptyState";
import { GuidePage } from "@/components/GuidePage";
import { PhiBanner } from "@/components/PhiBanner";
import { AppealTypeDialog } from "@/components/AppealTypeDialog";
import { DocumentViewer } from "@/components/DocumentViewer";
import { SidePanel } from "@/components/SidePanel";
import { LetterPreview } from "@/components/LetterView";
import { Toaster, toast } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useGetSession } from "@/hooks/api";
import { useAppStore } from "@/store/app";

export default function App() {
  const view = useAppStore((s) => s.view);
  const letterHtml = useAppStore((s) => s.letterHtml);
  const sessionId = useAppStore((s) => s.sessionId);
  const setSessionId = useAppStore((s) => s.setSessionId);
  const setView = useAppStore((s) => s.setView);
  const guideOpen = useAppStore((s) => s.guideOpen);

  const [bannerDismissed, setBannerDismissed] = useState(true);

  // ``?sessionId=...`` in the URL pre-loads an existing session so it
  // can be shared / reopened. Hydrate from the URL only *once* on mount,
  // then let the store own the session pointer. (Re-reading on every
  // ``sessionId`` change would fight reset()/New session: clearing the
  // store would immediately re-import the id the sync effect is trying
  // to strip from the URL.)
  useEffect(() => {
    const url = new URL(window.location.href);
    const sid = url.searchParams.get("sessionId");
    if (sid) setSessionId(sid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The ``?sessionId=...`` in the URL points at a session the server no
  // longer has (e.g. the backend restarted and wiped in-memory state). The
  // API layer raised ``session:expired`` instead of toasting a 404 — drop
  // the stale pointer so the sync effect below clears the querystring and a
  // refresh starts clean, and tell the user once why their session is gone.
  useEffect(() => {
    const onExpired = () => {
      // Only react if we're actually pinned to a (now-dead) session.
      if (!useAppStore.getState().sessionId) return;
      setSessionId(null);
      toast.info("That session is no longer available — starting a new one.");
    };
    window.addEventListener("session:expired", onExpired);
    return () => window.removeEventListener("session:expired", onExpired);
  }, [setSessionId]);

  // Keep the URL in sync with the active session so it can be copied /
  // shared / refreshed without losing the session. ``replaceState`` keeps
  // it out of the back/forward history.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (sessionId) {
      if (url.searchParams.get("sessionId") === sessionId) return;
      url.searchParams.set("sessionId", sessionId);
    } else {
      if (!url.searchParams.has("sessionId")) return;
      url.searchParams.delete("sessionId");
    }
    window.history.replaceState(null, "", url.toString());
  }, [sessionId]);

  const { data: session } = useGetSession(sessionId);
  const docCount = session?.documents?.length ?? 0;
  // Once any entry action fires, we already know docs are on their
  // way — flip into the working layout immediately so the unified
  // pending UX (placeholder rows + spinner) does the waiting.
  const pendingCount = useAppStore((s) => s.pendingEntries.length);

  // Auto-flip back to Analysis when the letter is cleared (e.g. New session).
  useEffect(() => {
    if (!letterHtml && view === "letter") setView("analysis");
  }, [letterHtml, view, setView]);

  // `--chrome-h` is the combined height of Header (56px) + optional PhiBanner
  // (~41px). Children that need to fill the remaining viewport read it via
  // `calc(100vh - var(--chrome-h, 56px))`.
  const chromeStyle = {
    "--chrome-h": bannerDismissed ? "56px" : "97px",
  } as CSSProperties;

  return (
    <TooltipProvider delayDuration={150}>
      <div className="min-h-screen bg-slate-50 text-foreground" style={chromeStyle}>
        <Header />
        {!bannerDismissed && <PhiBanner onDismiss={() => setBannerDismissed(true)} />}

        {guideOpen ? (
          <GuidePage />
        ) : docCount === 0 && pendingCount === 0 ? (
          <EmptyState />
        ) : (
          <div
            className="grid"
            style={{
              gridTemplateColumns: "1fr 440px",
              height: "calc(100vh - var(--chrome-h, 56px))",
            }}
          >
            {view === "letter" ? <LetterPreview /> : <DocumentViewer />}
            <SidePanel />
          </div>
        )}

        <AppealTypeDialog />
        <Toaster />

        <span
          className="fixed bottom-2 left-2 z-50 pointer-events-none select-none font-mono text-[10px] tabular-nums text-slate-400/80"
          title={`Build v${__APP_VERSION__}`}
        >
          v{__APP_VERSION__}
        </span>
      </div>
    </TooltipProvider>
  );
}
